import api from './serviceApi'; // Import the axios instance

const mailMessageApi = {
    /**
     * Fetch a specific mail message by its code.
     * @param {string} code - The unique code of the mail message.
     * @param {Object} params - Additional query parameters, e.g., `id` and `include`.
     * @returns {Promise<Object>} - The mail message data.
     */
    getMailMessageByCode: async (code, params = {}) => {
        try {
            const response = await api.get(`/MailMessage/${code}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching mail message by code:', error);
            throw error;
        }
    },

    /**
     * Fetch a specific mail message by its code.
     * @param {string} code - The unique code of the mail message.
     * @returns {Promise<Object>} - The mail message data.
     */
    resendMailMessageByCode: async (code) => {
        try {
            const response = await api.get(`/MailMessage/resend/${code}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching mail message by code:', error);
            throw error;
        }
    },

    /**
     * Export mail messages with specified filters.
     * @param {Object} params - Query parameters, e.g., `quantityMax`, `isActive`, `term`, `orderBy`, and `include`.
     * @returns {Promise<Object>} - The exported mail messages.
     */
    exportMailMessages: async (params) => {
        try {
            const response = await api.get('/MailMessage/export', { params });
            return response.data;
        } catch (error) {
            console.error('Error exporting mail messages:', error);
            throw error;
        }
    },

    /**
     * Fetch a paginated list of mail messages.
     * @param {Object} params - Query parameters, e.g., `page`, `quantity`, `isActive`, `term`, `orderBy`, and `include`.
     * @returns {Promise<Object>} - The paginated list of mail messages.
     */
    getMailMessagesPaginated: async (params) => {
        try {
            const response = await api.get('/MailMessage/pagged', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching paginated mail messages:', error);
            throw error;
        }
    },

    /**
     * Fetch all mail messages with specified filters.
     * @param {Object} params - Query parameters, e.g., `include`.
     * @returns {Promise<Object>} - The list of mail messages.
     */
    getMailMessages: async (params = {}) => {
        try {
            const response = await api.get('/MailMessage', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching mail messages:', error);
            throw error;
        }
    },

    /**
     * Create a new mail message.
     * @param {Object} data - The mail message payload.
     * @returns {Promise<Object>} - The created mail message.
     */
    createMailMessage: async (data) => {
        try {
            const response = await api.post('/MailMessage', data);
            return response.data;
        } catch (error) {
            console.error('Error creating mail message:', error);
            throw error;
        }
    },

    /**
     * Update an existing mail message.
     * @param {Object} data - The updated mail message payload.
     * @returns {Promise<Object>} - The updated mail message.
     */
    updateMailMessage: async (data) => {
        try {
            const response = await api.put('/MailMessage', data);
            return response.data;
        } catch (error) {
            console.error('Error updating mail message:', error);
            throw error;
        }
    },

    /**
     * Delete a mail message.
     * @param {Object} data - The payload for deleting a mail message.
     * @returns {Promise<Object>} - The deleted mail message response.
     */
    deleteMailMessage: async (data) => {
        try {
            const response = await api.delete('/MailMessage', { data });
            return response.data;
        } catch (error) {
            console.error('Error deleting mail message:', error);
            throw error;
        }
    },
};

export default mailMessageApi;
